/**
 * AdminShell — operator-grade shell for /admin/*.
 *
 * Same visual language as R1 AppShell but with:
 *   - admin-specific left nav (KPIs / Players / Deposits / …)
 *   - gold active-accent (vs player pink accent)
 *   - top bar shows "Admin panel" + ADMIN badge + notifications + logout
 *   - no wallet chip (admin has no player wallet)
 *
 * Preserves the `nav-<slug>` and `admin-logout-btn` data-testids from the
 * legacy Admin.jsx so Phase-4 tests keep passing.
 */
import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowDownToLine, ArrowUpFromLine, Banknote, Bell, ChevronLeft, ChevronRight,
  Gift, Grid, IdCard, Image, KeyRound, LayoutDashboard, LogOut, Menu, MessageSquare,
  Search, ShieldCheck, Sliders, Trophy, Users, X,
} from "lucide-react";

import { useAuth } from "../../context/AuthContext";
import { Badge } from "./Badge";

const SIDEBAR_KEY = "brg.admin.sidebar.collapsed";

const NAV = [
  { key: "overview",       to: "/admin",                    icon: LayoutDashboard, label: "Overview", end: true },
  { key: "players",        to: "/admin/players",            icon: Users,           label: "Players" },
  { key: "deposits",       to: "/admin/deposits",           icon: ArrowDownToLine, label: "Deposits" },
  { key: "withdrawals",    to: "/admin/withdrawals",        icon: ArrowUpFromLine, label: "Withdrawals" },
  { key: "transactions",   to: "/admin/transactions",       icon: Banknote,        label: "Transactions" },
  { key: "kyc",            to: "/admin/kyc",                icon: IdCard,          label: "KYC" },
  { key: "chat",           to: "/admin/chat",               icon: MessageSquare,   label: "Support chat" },
  { key: "kpis",           to: "/admin/kpis",               icon: Sliders,         label: "KPIs" },
  { key: "bonuses",        to: "/admin/bonuses",            icon: Gift,            label: "Bonuses" },
  { key: "sportsbook",     to: "/admin/sportsbook",         icon: Trophy,          label: "Sportsbook" },
  { key: "games",          to: "/admin/games",              icon: Grid,            label: "Games registry" },
  { key: "game-config",    to: "/admin/game-config",        icon: Sliders,         label: "Game config" },
  { key: "image-library",  to: "/admin/image-library",      icon: Image,           label: "Image library" },
  { key: "rg-policy",      to: "/admin/rg-policy",          icon: ShieldCheck,     label: "RG policy" },
  { key: "providers",      to: "/admin/providers",          icon: KeyRound,        label: "Providers" },
];

export default function AdminShell({ children, pageTitle = null }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === "1"; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  useEffect(() => { try { localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0"); } catch {} }, [collapsed]);

  const handleLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen text-ink" data-testid="admin-shell">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={
          "fixed top-0 left-0 h-full z-40 bg-elevated/95 backdrop-blur border-r border-line " +
          "transition-all duration-200 ease-out " +
          (mobileOpen ? "translate-x-0 " : "-translate-x-full lg:translate-x-0 ") +
          (collapsed ? "w-[260px] lg:w-[72px]" : "w-[260px]")
        }
      >
        <SidebarContent
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          onNav={() => setMobileOpen(false)}
          onLogout={handleLogout}
          user={user}
        />
      </aside>

      {mobileOpen && (
        <button
          className="fixed inset-0 z-30 bg-black/70 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          data-testid="admin-sidebar-backdrop"
        />
      )}

      {/* Main column */}
      <div
        className={
          "min-h-screen flex flex-col transition-all duration-200 " +
          (collapsed ? "lg:pl-[72px]" : "lg:pl-[260px]")
        }
      >
        <AdminTopBar
          onOpenSidebar={() => setMobileOpen(true)}
          user={user}
          pageTitle={pageTitle}
          avatarOpen={avatarOpen}
          setAvatarOpen={setAvatarOpen}
          onLogout={handleLogout}
        />
        <main className="flex-1 min-w-0 px-4 sm:px-8 py-6">{children}</main>
        <AdminFooter />
      </div>
    </div>
  );
}

function SidebarContent({ collapsed, setCollapsed, onNav, onLogout, user }) {
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Logo */}
      <div className={"flex items-center " + (collapsed ? "justify-center px-2" : "justify-between px-5") + " h-16 border-b border-line"}>
        <Link to="/" className="flex items-center gap-2 group" data-testid="admin-sidebar-logo">
          <span className="h-8 w-8 rounded-lg bg-gold-gradient flex items-center justify-center font-display italic text-[#0B0E1E] text-lg shadow-glow-gold">B</span>
          {!collapsed && (
            <div className="leading-tight">
              <div className="font-display italic text-lg text-ink">Bra Game</div>
              <div className="text-[9px] tracking-[0.3em] uppercase text-gold">Control room</div>
            </div>
          )}
        </Link>
        {!collapsed && (
          <button
            onClick={() => setCollapsed(true)}
            className="hidden lg:flex items-center justify-center w-6 h-6 rounded text-ink-3 hover:text-ink hover:bg-highest"
            aria-label="Collapse sidebar"
            data-testid="admin-sidebar-collapse-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-3 space-y-1 px-3">
        {collapsed && (
          <button
            onClick={() => setCollapsed(false)}
            className="w-full h-10 flex items-center justify-center rounded-lg text-ink-3 hover:text-ink hover:bg-highest mb-2"
            aria-label="Expand sidebar"
            data-testid="admin-sidebar-expand-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
        {NAV.map((n) => (
          <AdminNavItem key={n.key} to={n.to} end={n.end} icon={n.icon} label={n.label} collapsed={collapsed} onClick={onNav}
            testid={`nav-${n.key === "overview" ? "overview" : n.key}`} />
        ))}
      </nav>

      <div className={"border-t border-line " + (collapsed ? "p-2 space-y-2" : "p-4 space-y-3")}>
        {!collapsed && user && (
          <div className="text-[10px] tracking-[0.25em] uppercase text-ink-3 truncate" data-testid="admin-user-email">
            {user.email}
          </div>
        )}
        {user && (
          <button
            onClick={onLogout}
            className={"flex items-center gap-2 text-xs text-ink-3 hover:text-danger transition " + (collapsed ? "justify-center w-full" : "")}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" />
            {!collapsed && "Log out"}
          </button>
        )}
      </div>
    </div>
  );
}

function AdminNavItem({ to, end, icon: Icon, label, collapsed, onClick, testid }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        "flex items-center gap-3 rounded-lg text-sm transition group relative " +
        (collapsed ? "justify-center h-10 px-0" : "px-3 py-2.5") + " " +
        (isActive
          ? "bg-gold/10 text-gold border-l-2 border-gold rounded-l-none"
          : "text-ink-2 hover:text-ink hover:bg-highest")
      }
      data-testid={testid}
    >
      <Icon className="w-4 h-4 shrink-0" />
      {!collapsed && <span className="flex-1">{label}</span>}
    </NavLink>
  );
}

function AdminTopBar({ onOpenSidebar, user, pageTitle, avatarOpen, setAvatarOpen, onLogout }) {
  return (
    <header
      className="sticky top-0 z-30 h-16 bg-base/85 backdrop-blur border-b border-line px-3 sm:px-6 flex items-center gap-3"
      data-testid="admin-topbar"
    >
      <button
        onClick={onOpenSidebar}
        className="lg:hidden w-9 h-9 rounded-lg text-ink-2 hover:text-ink hover:bg-highest flex items-center justify-center"
        aria-label="Open menu"
        data-testid="admin-topbar-menu-btn"
      >
        <Menu className="w-5 h-5" />
      </button>

      <div className="flex items-center gap-3">
        <Badge variant="gold">
          <ShieldCheck className="w-3 h-3 mr-1" /> Admin
        </Badge>
        {pageTitle && (
          <span className="text-[11px] tracking-[0.3em] uppercase text-ink-3 whitespace-nowrap hidden md:block">
            {pageTitle}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 ml-auto">
        <button
          className="relative w-9 h-9 rounded-lg text-ink-2 hover:text-ink hover:bg-highest hidden sm:flex items-center justify-center"
          aria-label="Notifications"
          data-testid="admin-topbar-bell"
        >
          <Bell className="w-4 h-4" />
        </button>
        {user && (
          <div className="relative">
            <button
              onClick={() => setAvatarOpen(!avatarOpen)}
              className="flex items-center gap-2 pl-2 pr-3 h-9 rounded-lg border border-line hover:bg-highest text-sm"
              data-testid="admin-topbar-avatar-btn"
            >
              <span className="w-6 h-6 rounded-full bg-gold-gradient flex items-center justify-center text-[#0B0E1E] text-[11px] font-semibold shadow-glow-gold">
                {(user.email || "").slice(0, 1).toUpperCase()}
              </span>
            </button>
            {avatarOpen && (
              <>
                <button className="fixed inset-0 z-40" onClick={() => setAvatarOpen(false)} aria-label="close" />
                <div className="absolute right-0 mt-2 w-64 bg-highest border border-line rounded-xl shadow-card-lg z-50" data-testid="admin-topbar-avatar-menu">
                  <div className="p-4 border-b border-line">
                    <div className="text-[10px] tracking-widest uppercase text-ink-3">Signed in</div>
                    <div className="text-sm text-ink truncate">{user.email}</div>
                    <div className="mt-2"><Badge variant="gold">ADMIN</Badge></div>
                  </div>
                  <div className="p-2">
                    <Link to="/dashboard" onClick={() => setAvatarOpen(false)} className="block px-3 py-2 rounded-md text-sm text-ink-2 hover:text-ink hover:bg-elevated">Player view</Link>
                    <button onClick={() => { onLogout(); setAvatarOpen(false); }} className="w-full text-left px-3 py-2 rounded-md text-sm text-danger hover:bg-elevated">
                      <LogOut className="w-3.5 h-3.5 inline mr-2" />Log out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
}

function AdminFooter() {
  return (
    <footer className="mt-auto border-t border-line py-4 px-6" data-testid="admin-footer">
      <div className="text-[10px] tracking-widest uppercase text-ink-3">
        © 2026 Bra Game N.V. · Operator console · Anjouan License #ALSI-XXXXXX-XX
      </div>
    </footer>
  );
}
